import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { CacheService } from './cache.service';
import { Constants } from 'app/utility/constants';
import { DataWidgetDataType, SaleStatus, SaleStatusFilter, SaleTypeFilter, SecurityRight, TabHeaderEnum } from 'app/utility/enums';
import { Observable, Subject } from 'rxjs';
import { DataWidgetResultModel, DTODataWidgetFiltersListModel, DTODataWidgetFiltersModel, DTODataWidgetSalesFilters } from 'app/models/dms/dataWidget.model';
import { Router } from '@angular/router';
import { UserService } from './user.service';
import { WebViewMessageCommandModel } from 'app/models/shared/webViewMessageCommand.model';
import { EmbeddedViewConstants } from 'app/utility/viewConstants';
import { StorageService } from './storage.service';
import { DateHelper } from 'app/utility/datehelper';
import { throwError } from 'rxjs';

@Injectable({
	providedIn: 'root'
})
export class DashboardService
{

	constructor(
		private httpClient: HttpClient,
		private cache: CacheService,
		private router: Router,
		private userService: UserService,
		private storage: StorageService
	)
	{
		let overrideClientId = localStorage.getItem(
			Constants.LocalStorage_OverrideClientID
		);

		if (overrideClientId)
		{
			this.isEmbeddedClient = true;
		}
	}

	dealershipId: string = Constants.EmptyGuid;
	dateSelectionId: string = '0';
	dateFrom: Date = new Date(Constants.DateMinimum);
	dateTo: Date = new Date(Constants.DateMaximum);

	salesFilterChanged = new Subject<DTODataWidgetSalesFilters>();

	isEmbeddedClient: boolean = false;

	getCachedDataWidget(ignoreCache: boolean, widgetDataType: DataWidgetDataType, dealershipId: string, dateFrom: Date, dateTo: Date, filters: DTODataWidgetFiltersModel = null): Observable<DataWidgetResultModel>
	{
		// Ensure the user has the right to view this widget
		if(!this.hasWidgetSecurityRight(widgetDataType)){
			return throwError(() => ({
				error: {
				entityDataJson: JSON.stringify({
					Message: "You do not have the security rights to perform this action.",
					StatusCode: 403,
					Payload: ""
				})
				}
			}));
		}

		let cacheKey: string = 'dataWidget' + widgetDataType + dealershipId + dateFrom.toISOString() + dateTo.toISOString();
		let cacheMilliseconds: number = 1 * 5 * 60000; //5 minutes
		//let cacheMilliseconds: number = 1 * 5 * 1000; //5 seconds
		let retryIfLengthIsZero = false;

		if (ignoreCache)
		{
			this.cache.clear(cacheKey);
		}

		if (!this.userService.mcMinVersion("3.2.20"))
		{
			return this.cache.get(cacheKey, this.getDataWidget(widgetDataType, dealershipId, dateFrom, dateTo), cacheMilliseconds, retryIfLengthIsZero);
		}
		else
		{
			return this.cache.get(cacheKey, this.getFilteredDataWidget(widgetDataType, dealershipId, dateFrom, dateTo, filters), cacheMilliseconds, retryIfLengthIsZero);
		}
	}

	private hasWidgetSecurityRight(widgetDataType: DataWidgetDataType) : boolean {		
		// Ensure the user has the right to view this widget
		switch (widgetDataType) {
			case DataWidgetDataType.Vehicle_Inventory:
			case DataWidgetDataType.Vehicle_Inventory_Breakdown:
				return this.userService.hasUserRight(SecurityRight.Vehicles);				
			case DataWidgetDataType.Sales_SoldProjected:
			case DataWidgetDataType.Sales_BreakdownSummary:
				return this.userService.hasUserRight(SecurityRight.Sale);				
			case DataWidgetDataType.Opportunties_Summary:
			case DataWidgetDataType.FinanceCentral_Summary:
			case DataWidgetDataType.Marketing_Summary:
				return this.userService.hasUserRight(SecurityRight.Opport);
			case DataWidgetDataType.BuyerScore_Summary:
			case DataWidgetDataType.Needacar_Summary:
				//Public data, no security right check
				return true;				
			default:
				return false;
		}		
	}

	//Obsolete as of 3.2.19
	private getDataWidget(widgetDataType: DataWidgetDataType, dealershipId: string, dateFrom: Date, dateTo: Date): Observable<DataWidgetResultModel>
	{
		let httpParams = new HttpParams();

		httpParams = httpParams.set('dealershipId', dealershipId);
        httpParams = httpParams.set('dateFrom', DateHelper.getDateTimeAsFormat(dateFrom, Constants.DateFormatShortISO));
        httpParams = httpParams.set('dateTo', DateHelper.getDateTimeAsFormat(dateTo, Constants.DateFormatShortISO));

		return this.httpClient.get<DataWidgetResultModel>(Constants.ApiUrlReporting + `dashboard/${widgetDataType}/dataWidget`, { params: httpParams });
	}

	private getFilteredDataWidget(widgetDataType: DataWidgetDataType, dealershipId: string, dateFrom: Date, dateTo: Date, widgetFilters: DTODataWidgetFiltersModel): Observable<DataWidgetResultModel>
	{

		if (!widgetFilters)
		{
			widgetFilters = new DTODataWidgetFiltersModel();
		}

		widgetFilters.dateFrom = dateFrom;
		widgetFilters.dateTo = dateTo;
		widgetFilters.dealershipId = dealershipId;
		widgetFilters.widgetDataType = widgetDataType;
		return this.httpClient.post<DataWidgetResultModel>(Constants.ApiUrlReporting + `dashboard/${widgetDataType}/dataWidget`, widgetFilters);
	}

	navigateToEntities(widgetDataType: DataWidgetDataType, dealershipId: string, dateFrom: Date, dateTo: Date, fallbackRoute: string = '')
	{
		let overrideClientId = localStorage.getItem(
			Constants.LocalStorage_OverrideClientID
		);

        const startDate = DateHelper.getDateTimeAsFormat(dateFrom, Constants.DateFormatShortISO);
        const endDate = DateHelper.getDateTimeAsFormat(dateTo, Constants.DateFormatShortISO);

		// Is embedded in a different client
		if (overrideClientId)
		{
			let command = new WebViewMessageCommandModel();
			switch (widgetDataType)
			{
				case DataWidgetDataType.Vehicle_Inventory:
				case DataWidgetDataType.Vehicle_Inventory_Breakdown:
					command.viewCommand = EmbeddedViewConstants.TabView_Open;
					command.customField1 = TabHeaderEnum.Vehicles.toString();
					command.searchParamWrap = {
						searchParamVehicles: {
							dealershipId: dealershipId,
							vehicleSoldState: Constants.Vehicle_Status_Unsold,
							searchText: ''
						},
						searchParamSales: null,
						searchParamTasks: null,
					};

					this.userService.sendChromeMessageCommand(command);

					break;

				case DataWidgetDataType.Sales_SoldProjected:
				case DataWidgetDataType.Sales_BreakdownSummary:
					command.viewCommand = EmbeddedViewConstants.TabView_Open;
					command.customField1 = TabHeaderEnum.Sales.toString();
					command.searchParamWrap = {
						searchParamVehicles: null,
						searchParamSales: {
							dealershipId: dealershipId,
							saleStatus: SaleStatus.All.toString(),
							searchText: '',
                            startDate,
                            endDate,
						},
						searchParamTasks: null,
					};

					this.userService.sendChromeMessageCommand(command);

					break;

				case DataWidgetDataType.Opportunties_Summary:
					command.viewCommand = EmbeddedViewConstants.TabView_Open;
					command.customField1 = TabHeaderEnum.Opportunities.toString();
					command.searchParamWrap = {
						searchParamVehicles: null,
						searchParamSales: null,
						searchParamTasks: null,
					};

					this.userService.sendChromeMessageCommand(command);

					break;


				case DataWidgetDataType.FinanceCentral_Summary:
					command.viewCommand = EmbeddedViewConstants.TabView_Open;
					command.customField1 = TabHeaderEnum.FinanceCentral.toString();
					command.searchParamWrap = {
						searchParamVehicles: null,
						searchParamSales: null,
						searchParamTasks: null,
					};

					this.userService.sendChromeMessageCommand(command);

					break;
			}

		} else
		{
			switch (widgetDataType)
			{
				case DataWidgetDataType.Vehicle_Inventory_Breakdown:
					this.router.navigate(['/vehicles'], {
						queryParams: {
							dealershipId: dealershipId, vehicleSoldState: Constants.Vehicle_Status_Unsold,
							view: 'custom'
						}
					});
					break;

				case DataWidgetDataType.Sales_BreakdownSummary:
					this.router.navigate(['/sales'], {
						queryParams: {
							dealershipId: dealershipId,
							saleStatus: SaleStatus.All,
							view: 'custom',
                            dateFrom: startDate,
                            dateTo: endDate,
						}
					});
					break;
				default:
					// console.log('navigate:', fallbackRoute);
					if (fallbackRoute)
					{
						if (fallbackRoute.startsWith('https://'))
						{
							// navigate to url
							window.open(fallbackRoute, '_blank');
						}
						else
						{
							// Navigate to internal route
							this.router.navigate([fallbackRoute]);
						}
					}
					break;
			}
		}
	}



	performModalAction(widgetDataType: DataWidgetDataType, ids: string[])
	{

		// Is embedded in a different client
		if (this.isEmbeddedClient)
		{
			let command = new WebViewMessageCommandModel();
			switch (widgetDataType)
			{
				case DataWidgetDataType.Vehicle_Inventory_Breakdown:
					command.viewCommand = EmbeddedViewConstants.VehicleEdit;
					command.ids = ids;
					command.customField1 = '';

					this.userService.sendChromeMessageCommand(command);

					break;

				case DataWidgetDataType.Sales_BreakdownSummary:

					command.viewCommand = EmbeddedViewConstants.SaleEdit;
					command.ids = ids;

					this.userService.sendChromeMessageCommand(command);

					break;

				case DataWidgetDataType.Marketing_Summary:
					command.viewCommand = EmbeddedViewConstants.ToolsTaskViewer;
					//command.ids = ids;
					command.searchParamWrap = {
						searchParamVehicles: null,
						searchParamSales: null,
						searchParamTasks: {
							searchText: '',
							taskIds: ids,
							taskStatus: '0',
						},
					};

					this.userService.sendChromeMessageCommand(command);

					break;
			}

		} else
		{
			// switch (widgetDataType) {
			//   case DataWidgetDataType.Vehicle_Inventory_Breakdown:
			//     this.router.navigate(['/vehicles'], {
			//       queryParams: {
			//         dealershipId: dealershipId, vehicleSoldState: Constants.Vehicle_Status_Unsold,
			//         view: 'custom'
			//       }
			//     });
			//     break;

			//   case DataWidgetDataType.Sales_BreakdownSummary:
			//     this.router.navigate(['/sales'], {
			//       queryParams: {
			//         dealershipId: dealershipId,
			//         saleStatus: SaleStatus.All,
			//         view: 'custom',
			//         dateFrom: dateFrom.toDateString(),
			//         dateTo: dateTo.toDateString()
			//       }
			//     });
			//     break;
			// }
		}



	}

	buildDateRangeErrorText(minDateFrom: Date): string
	{
		let currentDate = new Date();
		let minDate = new Date(minDateFrom);

		let diffInMilliseconds = currentDate.getTime() - minDate.getTime();

		let diffInYears = Math.floor(diffInMilliseconds / (1000 * 60 * 60 * 24 * 365.25));
		let diffInMonths = Math.floor((diffInMilliseconds % (1000 * 60 * 60 * 24 * 365.25)) / (1000 * 60 * 60 * 24 * 30));

		let message = "Date range must be ";
		if (diffInYears > 0)
		{
			message += `less than ${diffInYears} year${diffInYears > 1 ? 's' : ''} ago`;
			if (diffInMonths > 0)
			{
				message += ` and ${diffInMonths} month${diffInMonths > 1 ? 's' : ''} ago`;
			}
		} else if (diffInMonths > 0)
		{
			message += `less than ${diffInMonths} month${diffInMonths > 1 ? 's' : ''} ago`;
		}
		return message;
	}

	async getDashboardFilters()
	{
		let saleFilters = null;
		var saleFiltersJson = await this.storage.get(Constants.IonicStorage_DashboardSaleFilters, true);
		if (saleFiltersJson)
		{
			saleFilters = JSON.parse(saleFiltersJson);
		}
		var saleStatusFilter = saleFilters?.saleStatusFilter ?? SaleStatusFilter.IncludeAll;
		var saleTypeFilter = saleFilters?.saleTypeFilter ?? SaleTypeFilter.All;

		let salesBreakdownFilters = null;
		var salesBreakdownFiltersJson = await this.storage.get(Constants.IonicStorage_DashboardSaleBreakdownFilters, true);
		if (salesBreakdownFiltersJson)
		{
			salesBreakdownFilters = JSON.parse(salesBreakdownFiltersJson);
		}
		var saleStatusBreakdownFilter = salesBreakdownFilters?.saleStatusFilter ?? SaleStatusFilter.IncludeAll;
		var saleTypeBreakdownFilter = salesBreakdownFilters?.saleTypeFilter ?? SaleTypeFilter.All;


		//set number of filters based on whether they are set to all or not
		var numberOfSalesFilters = 0;
		if (saleStatusFilter != SaleStatusFilter.IncludeAll)
		{
			numberOfSalesFilters++;
		}
		if (saleTypeFilter != SaleTypeFilter.All)
		{
			numberOfSalesFilters++;
		}

		var filters = new DTODataWidgetFiltersListModel();

		filters.salesFilters.saleStatus = saleStatusFilter;
		filters.salesFilters.saleType = saleTypeFilter;

		//Breakdown is a mirror of sale at the moment which is why it isnt included in the counts below
		filters.salesBreakdownFilters.saleStatus = saleStatusBreakdownFilter;
		filters.salesBreakdownFilters.saleType = saleTypeBreakdownFilter;

		var numberOfEachFilter =
		{
			sales: numberOfSalesFilters
		}

		return { filters, numberOfEachFilter };

	}

}
